import { Injectable, inject } from "@angular/core";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import {
  RON_MONITOR_CLOSING_STATES,
  RonMonitorAddressWire,
  RonMonitorBoardWire,
  RonMonitorClosingWire,
  RonMonitorNotaryLaneWire,
  RonMonitorOrderDetailWire,
  RonMonitorOrderParticipantWire,
} from "../models/ron-monitor.api-contract";
import { RonMonitorDataSource } from "../models/ron-monitor.data-source";
import {
  BoardSegmentFilter,
  ClosingDetail,
  ClosingDetailClient,
  ClosingIcon,
  ClosingPill,
  NotaryLane,
  NotarySegment,
  RonMonitorBoard,
  RonMonitorSummary,
  SlotVariant,
} from "../models/ron-monitor.models";
import {
  DayKind,
  NO_VALUE,
  dayKindFor,
  laneStatusFor,
} from "../ron-closing-monitor.utils";
import { RonMonitorClock } from "./ron-monitor-clock.service";
import { RonMonitorService } from "./ron-monitor.service";

const STATE_VARIANTS: Record<string, SlotVariant> = {
  [RON_MONITOR_CLOSING_STATES.scheduled]: "scheduled",
  [RON_MONITOR_CLOSING_STATES.checkIn]: "check-in",
  [RON_MONITOR_CLOSING_STATES.preSign]: "pre-sign",
  [RON_MONITOR_CLOSING_STATES.inSession]: "in-session",
  [RON_MONITOR_CLOSING_STATES.completed]: "completed",
  [RON_MONITOR_CLOSING_STATES.cancelled]: "canceled",
  [RON_MONITOR_CLOSING_STATES.stuck]: "stuck",
  [RON_MONITOR_CLOSING_STATES.borrowerDelayed]: "delayed",
  [RON_MONITOR_CLOSING_STATES.notaryDelayed]: "delayed",
  [RON_MONITOR_CLOSING_STATES.runningOver]: "over",
};

/** Borrower and notary delay share one variant, so the icon is what tells them apart. */
const STATE_ICONS: Record<string, ClosingIcon | null> = {
  [RON_MONITOR_CLOSING_STATES.scheduled]: null,
  [RON_MONITOR_CLOSING_STATES.checkIn]: "person",
  [RON_MONITOR_CLOSING_STATES.preSign]: "stamp",
  [RON_MONITOR_CLOSING_STATES.inSession]: "stamp",
  [RON_MONITOR_CLOSING_STATES.completed]: null,
  [RON_MONITOR_CLOSING_STATES.cancelled]: null,
  [RON_MONITOR_CLOSING_STATES.stuck]: "person",
  [RON_MONITOR_CLOSING_STATES.borrowerDelayed]: "person",
  [RON_MONITOR_CLOSING_STATES.notaryDelayed]: "headphone",
  [RON_MONITOR_CLOSING_STATES.runningOver]: "stamp",
};

const ROLE_LABELS: Record<string, string> = {
  BORROWER: "Borrower",
  NBTH: "NBTH",
  SELLER: "Seller",
  WITNESS: "Witness",
  SIGNER: "Signer",
  ATTORNEY: "Attorney",
  SIGNINGAGENT: "Notary",
};

@Injectable()
export class ApiRonMonitorDataSource extends RonMonitorDataSource {
  private readonly ronMonitorService = inject(RonMonitorService);
  private readonly clock = inject(RonMonitorClock);

  getBoard(
    date: Date,
    segment: BoardSegmentFilter
  ): Observable<RonMonitorBoard> {
    return this.ronMonitorService
      .getBoard(date, segment)
      .pipe(map((wire) => this.toBoard(wire, date)));
  }

  getDetail(packageId: number): Observable<ClosingDetail> {
    return this.ronMonitorService
      .getDetail(packageId)
      .pipe(map((wire) => this.toDetail(wire, packageId)));
  }

  private toBoard(wire: RonMonitorBoardWire, date: Date): RonMonitorBoard {
    const closings = (wire?.closings ?? []).map((closing) =>
      this.toPill(closing)
    );
    const dayKind = this.dayKind(date);

    return {
      generatedAt: wire?.generatedAtUtc ?? this.clock.now().toISOString(),
      summary: this.toSummary(wire),
      lanes: (wire?.lanes ?? []).map((lane) =>
        this.toLane(lane, closings, dayKind)
      ),
      closings,
    };
  }

  private toSummary(wire: RonMonitorBoardWire): RonMonitorSummary {
    const summary = wire?.summary;

    return {
      scheduled: summary?.scheduled ?? 0,
      stuckClients: summary?.stuckClients ?? 0,
      borrowerDelayed: summary?.borrowerDelayed ?? 0,
      notaryDelayed: summary?.notaryDelayed ?? 0,
      runningOver: summary?.runningOver ?? 0,
      completed: summary?.completed ?? 0,
      completedScheduledDenominator: summary?.scheduledDenominator ?? 0,
      cancelled: summary?.cancelled ?? 0,
      openSlots: summary?.openSlots ?? null,
      stuckThresholdMinutes: summary?.stuckThresholdMinutes ?? 0,
    };
  }

  private toPill(closing: RonMonitorClosingWire): ClosingPill {
    return {
      packageId: closing.packageId,
      notaryId:
        closing.notaryAccountId === null ||
        closing.notaryAccountId === undefined
          ? null
          : String(closing.notaryAccountId),
      scheduledDateTime: closing.scheduledDateTime ?? "",
      variant: STATE_VARIANTS[closing.state] ?? "scheduled",
      delayMinutes: closing.delayMinutes ?? null,
      icon: STATE_ICONS[closing.state] ?? null,
      // Empty, not NO_VALUE: search matches on these, and a placeholder would match.
      loanNumber: closing.loanIdentifier ?? "",
      orderNumber: closing.partnerOrderIdentifier ?? "",
    };
  }

  private toLane(
    lane: RonMonitorNotaryLaneWire,
    closings: ClosingPill[],
    dayKind: DayKind
  ): NotaryLane {
    const notaryId = String(lane.notaryAccountId);
    const owned = closings.filter((pill) => pill.notaryId === notaryId);

    return {
      notaryId,
      name: this.personName(lane.firstName, lane.lastName),
      segment: this.toSegment(lane.segment),
      currentStatus: laneStatusFor(owned, dayKind),
    };
  }

  private toSegment(segment: string | null): NotarySegment | null {
    const normalized = segment?.trim().toUpperCase();

    if (normalized === "TSA") {
      return "TSA";
    }
    if (normalized === "PA" || normalized === "PPA") {
      return "PA";
    }
    return null;
  }

  /** The API has no status, currently or reason on this contract yet (feature 294771). */
  private toDetail(
    wire: RonMonitorOrderDetailWire,
    packageId: number
  ): ClosingDetail {
    const closing = wire?.closing;

    return {
      packageId: wire?.packageId ?? packageId,
      notary: {
        name: wire?.notary
          ? this.personName(wire.notary.firstName, wire.notary.lastName)
          : "Unassigned",
        status: NO_VALUE,
        currently: NO_VALUE,
        reason: null,
        phone: wire?.notary?.phoneNumber ?? null,
      },
      clients: (wire?.clients ?? []).map((client) => this.toClient(client)),
      closing: {
        loanNumber: closing?.loanIdentifier ?? NO_VALUE,
        signingId: closing?.partnerOrderIdentifier ?? NO_VALUE,
        propertyAddress: this.formatAddress(closing?.propertyAddress ?? null),
        scheduledAt: closing?.scheduledDateTime ?? "",
        completedAt: closing?.actualCompleteDateTime ?? null,
        rescheduledTo: null,
      },
    };
  }

  private toClient(
    client: RonMonitorOrderParticipantWire
  ): ClosingDetailClient {
    const role = client.role?.trim().toUpperCase();

    return {
      name: this.personName(client.firstName, client.lastName),
      role: (role && ROLE_LABELS[role]) || client.role || NO_VALUE,
      currently: NO_VALUE,
      phone: client.phoneNumber ?? null,
    };
  }

  /** The board procedure returns NULL names when identity cannot be resolved. */
  private personName(
    firstName: string | null,
    lastName: string | null
  ): string {
    const name = [firstName, lastName]
      .filter((part) => !!part?.trim())
      .join(" ")
      .trim();

    return name || "Unknown";
  }

  private formatAddress(address: RonMonitorAddressWire | null): string {
    if (!address) {
      return NO_VALUE;
    }

    const street = [address.streetAddress1, address.streetAddress2]
      .filter((part) => !!part?.trim())
      .join(" ");
    const zip = [address.zipCode, address.zip4]
      .filter((part) => !!part?.trim())
      .join("-");
    const cityStateZip = [
      address.city,
      [address.stateCode, zip].filter((part) => !!part?.trim()).join(" "),
    ]
      .filter((part) => !!part?.trim())
      .join(", ");

    const formatted = [street, cityStateZip]
      .filter((part) => part.length > 0)
      .join(", ");

    return formatted || NO_VALUE;
  }

  private dayKind(date: Date): DayKind {
    return dayKindFor(date, this.clock.now());
  }
}
